"use client";

import { useMemo, useState } from "react";

export function LessonJsonBuilder() {
    const [builderLessonType, setBuilderLessonType] = useState("standard");
    const [builderLessonId, setBuilderLessonId] = useState("beginner-lesson-01");
    const [builderLessonTitle, setBuilderLessonTitle] = useState("");
    const [builderCourseLevel, setBuilderCourseLevel] = useState("Beginner");
    const [builderConceptsCovered, setBuilderConceptsCovered] = useState("");
    const [builderDataSources, setBuilderDataSources] = useState("AI Engineering Curriculum\nLibrary Documentation");
    const [builderTechnicalAbstract, setBuilderTechnicalAbstract] = useState("");
    const [builderCopySuccess, setBuilderCopySuccess] = useState<string | null>(null);
    const [builderConcepts, setBuilderConcepts] = useState<Array<{
        concept_name: string;
        core_logic: string;
        mathematical_basis: string;
        implementation_steps_raw: string;
        language: string;
        libraries_raw: string;
        code: string;
        common_failures_raw: string;
        limitations: string;
        scalability_concerns: string;
    }>>([
        {
            concept_name: "",
            core_logic: "",
            mathematical_basis: "N/A — beginner tier",
            implementation_steps_raw: "",
            language: "python",
            libraries_raw: "",
            code: "",
            common_failures_raw: "",
            limitations: "",
            scalability_concerns: "",
        },
    ]);

    const canonicalLessonJson = useMemo(() => {
        const splitLines = (value: string) =>
            value
                .split("\n")
                .map((line) => line.trim())
                .filter(Boolean);

        const conceptsCovered = splitLines(builderConceptsCovered);
        const dataSources = splitLines(builderDataSources);

        const concepts = builderConcepts.map((concept, index) => {
            const implementationSteps = splitLines(concept.implementation_steps_raw).map((line, stepIndex) => {
                const [process = "", rationale = ""] = line.split("||").map((part) => part.trim());
                return {
                    step: stepIndex + 1,
                    process,
                    rationale,
                };
            });

            const commonFailures = splitLines(concept.common_failures_raw);
            const libraries = concept.libraries_raw
                .split(",")
                .map((lib) => lib.trim())
                .filter(Boolean);

            return {
                concept_name: concept.concept_name || `Concept ${index + 1}`,
                concept_index: index + 1,
                foundational_mechanics: {
                    core_logic: concept.core_logic,
                    mathematical_basis: concept.mathematical_basis || "N/A — beginner tier",
                },
                implementation_steps: implementationSteps,
                code_blueprint: {
                    language: concept.language || "N/A",
                    libraries,
                    code: concept.code || "N/A",
                },
                production_audits: {
                    common_failures: commonFailures,
                    limitations: concept.limitations,
                    scalability_concerns: concept.scalability_concerns || "N/A",
                },
            };
        });

        return {
            audit_meta: {
                lesson_type: builderLessonType || "standard",
                concepts_covered: conceptsCovered,
                lesson_id: builderLessonId,
                lesson_title: builderLessonTitle,
                course_level: builderCourseLevel,
                data_sources: dataSources,
            },
            technical_abstract: builderTechnicalAbstract,
            concepts,
        };
    }, [
        builderConcepts,
        builderConceptsCovered,
        builderCourseLevel,
        builderDataSources,
        builderLessonId,
        builderLessonTitle,
        builderLessonType,
        builderTechnicalAbstract,
    ]);

    const canonicalLessonJsonText = useMemo(
        () => JSON.stringify(canonicalLessonJson, null, 2),
        [canonicalLessonJson],
    );

    const addConceptBuilderRow = () => {
        setBuilderConcepts((prev) => [
            ...prev,
            {
                concept_name: "",
                core_logic: "",
                mathematical_basis: "N/A — beginner tier",
                implementation_steps_raw: "",
                language: "python",
                libraries_raw: "",
                code: "",
                common_failures_raw: "",
                limitations: "",
                scalability_concerns: "",
            },
        ]);
    };

    const updateConceptBuilderRow = (
        index: number,
        field: keyof (typeof builderConcepts)[number],
        value: string,
    ) => {
        setBuilderConcepts((prev) =>
            prev.map((concept, i) => (i === index ? { ...concept, [field]: value } : concept)),
        );
    };

    const removeConceptBuilderRow = (index: number) => {
        setBuilderConcepts((prev) => prev.filter((_, i) => i !== index));
    };

    const copyCanonicalJson = async () => {
        await navigator.clipboard.writeText(canonicalLessonJsonText);
        setBuilderCopySuccess("Copied JSON to clipboard.");
        setTimeout(() => setBuilderCopySuccess(null), 1500);
    };

    return (
        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-700 shadow-sm">
            <p className="text-sm font-semibold text-slate-900">Create canonical lesson JSON</p>
            <p className="text-xs text-slate-500">
                Build valid lesson JSON with form fields, then copy it into any lesson content box.
            </p>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <select
                    value={builderLessonType}
                    onChange={(e) => setBuilderLessonType(e.target.value)}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                >
                    <option value="standard">standard</option>
                    <option value="theory_only">theory_only</option>
                    <option value="setup_environment">setup_environment</option>
                    <option value="hands_on">hands_on</option>
                    <option value="deployment_infra">deployment_infra</option>
                    <option value="conceptual_ethics">conceptual_ethics</option>
                </select>
                <input
                    value={builderCourseLevel}
                    onChange={(e) => setBuilderCourseLevel(e.target.value)}
                    placeholder="course_level"
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
                <input
                    value={builderLessonId}
                    onChange={(e) => setBuilderLessonId(e.target.value)}
                    placeholder="lesson_id"
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
                <input
                    value={builderLessonTitle}
                    onChange={(e) => setBuilderLessonTitle(e.target.value)}
                    placeholder="lesson_title"
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
            </div>

            <textarea
                value={builderConceptsCovered}
                onChange={(e) => setBuilderConceptsCovered(e.target.value)}
                placeholder="concepts_covered (one per line)"
                rows={4}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
            <textarea
                value={builderDataSources}
                onChange={(e) => setBuilderDataSources(e.target.value)}
                placeholder="data_sources (one per line)"
                rows={3}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
            <textarea
                value={builderTechnicalAbstract}
                onChange={(e) => setBuilderTechnicalAbstract(e.target.value)}
                placeholder="technical_abstract"
                rows={6}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />

            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-900">Concepts</p>
                    <button
                        type="button"
                        onClick={addConceptBuilderRow}
                        className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                        Add concept
                    </button>
                </div>
                {builderConcepts.map((concept, index) => (
                    <div key={`concept-builder-${index}`} className="space-y-2 rounded-lg border border-slate-200 p-3">
                        <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold text-slate-500">Concept {index + 1}</p>
                            {builderConcepts.length > 1 ? (
                                <button
                                    type="button"
                                    onClick={() => removeConceptBuilderRow(index)}
                                    className="text-xs font-semibold text-red-600 hover:text-red-700"
                                >
                                    Remove
                                </button>
                            ) : null}
                        </div>
                        <input
                            value={concept.concept_name}
                            onChange={(e) => updateConceptBuilderRow(index, "concept_name", e.target.value)}
                            placeholder="concept_name"
                            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                        />
                        <textarea
                            value={concept.core_logic}
                            onChange={(e) => updateConceptBuilderRow(index, "core_logic", e.target.value)}
                            placeholder="foundational_mechanics.core_logic"
                            rows={3}
                            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                        />
                        <textarea
                            value={concept.implementation_steps_raw}
                            onChange={(e) => updateConceptBuilderRow(index, "implementation_steps_raw", e.target.value)}
                            placeholder={"implementation_steps (one per line as process || rationale)"}
                            rows={3}
                            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                        />
                        <input
                            value={concept.libraries_raw}
                            onChange={(e) => updateConceptBuilderRow(index, "libraries_raw", e.target.value)}
                            placeholder="libraries (comma-separated)"
                            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                        />
                        <textarea
                            value={concept.code}
                            onChange={(e) => updateConceptBuilderRow(index, "code", e.target.value)}
                            placeholder="code_blueprint.code"
                            rows={4}
                            className="w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs"
                        />
                        <textarea
                            value={concept.common_failures_raw}
                            onChange={(e) => updateConceptBuilderRow(index, "common_failures_raw", e.target.value)}
                            placeholder="common_failures (one per line)"
                            rows={3}
                            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                        />
                    </div>
                ))}
            </div>

            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-900">Generated JSON</p>
                    <button
                        type="button"
                        onClick={copyCanonicalJson}
                        className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
                    >
                        Copy JSON
                    </button>
                </div>
                {builderCopySuccess ? (
                    <p className="text-xs font-medium text-emerald-600">{builderCopySuccess}</p>
                ) : null}
                <textarea
                    value={canonicalLessonJsonText}
                    readOnly
                    rows={16}
                    className="w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-2 font-mono text-xs"
                />
            </div>
        </div>
    );
}

